/**
 * PayrollPeriodPicker - Wrapper around generic EntityPicker
 * 
 * Provides payrollPeriod-specific configuration and maintains backward compatibility
 * with any code that imports PayrollPeriodPickerField or PayrollPeriodPickerModal
 */

import React, { useMemo } from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';

// ── PayrollPeriod-specific configuration ────────────────────────────────────────
export const PAYROLLPERIOD_CONFIG = {
  apiEndpoint: "/api/PayrollPeriod/active",
  params: {},
  entityName: "PayrollPeriod",
  idField: "id",
  displayFields: {
    primary: "name",
    secondary: "id"
  },
  tableHeaders: ["Pay Type", "Cutoff Type", "Period"],
  tableAccessors: ["payTypeDescription", "identifier", "name"],
  searchFields: [
    { label: "All", field: "all", index: 0 },
    { label: "Description", field: "name", index: 1 },
    { label: "PayType", field: "payTypeDescription", index: 2 },
    { label: "Cutoff", field: "identifier", index: 2 },
  ],
  placeholder: "--Select payrollPeriod--"
};

// ── PayrollPeriodPickerField wrapper ────────────────────────────────────────────
/**
 * PayrollPeriodPickerField
 * 
 * Usage:
 * <PayrollPeriodPickerField
 *   label="PayrollPeriod"
 *   payrollPeriodId={form.payrollPeriodId}
 *   displayValue={form.payrollPeriodDescription}
 *   onSelect={(payrollPeriod) => {
 *     set("payrollPeriodId", payrollPeriod.id);
 *     set("payrollPeriodCode", payrollPeriod.code);
 *     set("payrollPeriodDescription", payrollPeriod.description);
 *   }}
 *   onClear={() => {
 *     set("payrollPeriodId", "");
 *     set("payrollPeriodCode", "");
 *     set("payrollPeriodDescription", "");
 *   }}
 *   disabled={!editable}
 * />
 */
export function PayrollPeriodPickerField({
  payrollPeriodId,       // payrollPeriod-specific prop name
  payTypeId = 0, // for adjustment of the URL endpoint
  payrollPeriodIdentifier = "", // for adjustment of the URL endpoint
  displayValue = "",
  onSelect,
  onClear,
  ...rest             // label, isRequired, error, disabled, etc.
}) {

  // easier way to update url link based on the api param needs
  // eg: /api/MedicalClearance/unused-completed-list/123
  const config = useMemo(() => {
    let endpoint = PAYROLLPERIOD_CONFIG.apiEndpoint;
    let params = PAYROLLPERIOD_CONFIG.params;

    if (payTypeId) {
      endpoint = `${endpoint}/paytype/${payTypeId}`;
    }

    if (payrollPeriodIdentifier) {
      endpoint = "/api/PayrollPeriod/get-by-identifier"; // Using the specific endpoint
      params = { identifier: payrollPeriodIdentifier }; // Map directly to what Backend expects
    }

    return {
      ...PAYROLLPERIOD_CONFIG,
      apiEndpoint: endpoint,
      params: params,
    };
  }, [payTypeId, payrollPeriodIdentifier]);

  return (
    <EntityPickerField
      {...rest}
      config={config}
      entityId={payrollPeriodId}
      displayValue={displayValue}
      onSelect={onSelect}
      onClear={onClear}
    />
  );
}

// ── PayrollPeriodPickerModal wrapper ────────────────────────────────────────────
/**
 * PayrollPeriodPickerModal
 * 
 * Usage (standalone):
 * <PayrollPeriodPickerModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   setData={(payrollPeriod) => {
 *     set("payrollPeriodId", payrollPeriod.id);
 *     set("payrollPeriodDescription", payrollPeriod.description);
 *   }}
 * />
 */
export function PayrollPeriodPickerModal({ isOpen, onClose, setData }) {
  return (
    <EntityPickerModal
      isOpen={isOpen}
      onClose={onClose}
      setData={setData}
      config={PAYROLLPERIOD_CONFIG}
    />
  );
}

// ── Exports (backward compatibility) ─────────────────────────────────────────
export { PayrollPeriodPickerField as default };